import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Mail, Phone, Undo2, Link2, Send } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RefundDialog from "@/pages/admin/RefundDialog";
import { api, errDetail, fmtUSD } from "@/lib/api";
import type { Booking } from "@/types";

export const STATUS: Record<string, { label: string; cls: string }> = {
  new: { label: "Nueva solicitud", cls: "bg-sky-100 text-sky-800" },
  contacted: { label: "Contactado", cls: "bg-indigo-100 text-indigo-800" },
  confirmed: { label: "Confirmada (bloqueada)", cls: "bg-emerald-100 text-emerald-800" },
  pending_payment: { label: "Pago pendiente", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Pagada en línea", cls: "bg-[#0B192C] text-[#D4AF37]" },
  cancelled: { label: "Cancelada", cls: "bg-slate-200 text-slate-600" },
  conflict: { label: "Conflicto de fecha", cls: "bg-red-100 text-red-700" },
  expired: { label: "Pago expirado", cls: "bg-slate-100 text-slate-500" },
};
const BLOCK_LABEL: Record<string, string> = { "4h": "4 h", "6h": "6 h", "8h": "8 h", overnight: "Pernocta" };
const FILTERS = [
  { k: "all", label: "Todas" }, { k: "contact", label: "Solicitudes" }, { k: "online", label: "En línea" },
  { k: "new", label: "Nuevas" }, { k: "paid", label: "Pagadas" }, { k: "confirmed", label: "Confirmadas" }, { k: "cancelled", label: "Canceladas" },
];

function BookingRow({ b, onChange, onRefund }: { b: Booking; onChange: (id: string, s: string) => void; onRefund: (b: Booking) => void }) {
  const st = STATUS[b.status] || { label: b.status, cls: "bg-slate-100" };
  const refunded = b.refunded_amount || 0;
  const balPaid = (b.balance_payments || []).reduce((s, x) => s + x.amount, 0);
  const remaining = Math.max(0, b.amount - (b.charge_amount ?? b.amount) - balPaid);
  const hasBalance = b.status === "paid" && b.mode === "online" && remaining > 0.01;
  const [sending, setSending] = useState(false);
  const sendLink = async (email: boolean) => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/balance-link`, null, { params: { send_email: email } });
      if (!email) { await navigator.clipboard.writeText(data.url); toast.success("Enlace copiado"); }
      else toast.success(`Enlace enviado a ${b.email}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setSending(false); }
  };
  return (
    <div data-testid={`admin-booking-${b.id}`} className="bg-white rounded-lg border border-slate-200 p-5 grid gap-4 md:grid-cols-[1.3fr_1.2fr_auto] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`admin-booking-status-${b.id}`}>{st.label}</span>
          <span className="text-[11px] uppercase tracking-wider text-slate-400">{b.mode === "online" ? "Pago en línea (Whop)" : "Solicitud de contacto"}</span>
        </div>
        <p className="mt-2 font-display text-xl text-[#0B192C]">{b.yacht_name}</p>
        <p className="text-sm text-slate-600">{b.date} · {BLOCK_LABEL[b.block]} · {b.guests} personas · <b>{fmtUSD(b.amount)}</b>
          {b.charge_amount != null && b.charge_amount < b.amount - 0.01 && b.mode === "online" && <span className="text-xs text-slate-500" data-testid={`admin-booking-deposit-${b.id}`}> · anticipo {fmtUSD(b.charge_amount)} · saldo {fmtUSD(remaining)}{balPaid > 0 && ` (pagado en línea ${fmtUSD(balPaid)})`}{b.status === "paid" && remaining <= 0.01 && " · liquidado"}</span>}
        </p>
        {(refunded > 0 || (b.refunds && b.refunds.length > 0)) && (
          <p className="mt-1 text-xs text-slate-500" data-testid={`admin-booking-refunded-${b.id}`}>Reembolsado: <b>{fmtUSD(refunded)}</b>{b.refunds?.[0]?.reason ? ` · ${b.refunds[0].reason}` : ""}</p>
        )}
      </div>
      <div className="min-w-0 text-sm text-slate-600 space-y-1">
        <p className="font-semibold text-[#0B192C]">{b.name}</p>
        <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#008C86] break-all"><Mail className="h-3.5 w-3.5 shrink-0" />{b.email}</a>
        <a href={`https://wa.me/${(b.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#008C86]"><Phone className="h-3.5 w-3.5" />{b.phone}</a>
        {b.notes && <p className="text-xs text-slate-500 italic">“{b.notes}”</p>}
      </div>
      <div className="flex flex-col gap-2">
        <Select value={b.status} onValueChange={(v) => onChange(b.id, v)}>
          <SelectTrigger className="w-full md:w-56" data-testid={`admin-booking-status-select-${b.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasBalance && (
          <div className="flex gap-2">
            <button onClick={() => sendLink(true)} disabled={sending} data-testid={`admin-booking-balance-send-${b.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0B192C] px-3 py-2 text-xs font-semibold text-white hover:bg-[#13294a] disabled:opacity-60"><Send className="h-3.5 w-3.5" />Enviar link de saldo</button>
            <button onClick={() => sendLink(false)} disabled={sending} data-testid={`admin-booking-balance-copy-${b.id}`} aria-label="Copiar enlace" className="inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-2 text-slate-600 hover:border-[#0B192C]"><Link2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {b.status === "paid" && (
          <button onClick={() => onRefund(b)} data-testid={`admin-booking-refund-btn-${b.id}`} className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"><Undo2 className="h-3.5 w-3.5" />Cancelar y reembolsar</button>
        )}
      </div>
    </div>
  );
}

export default function AdminBookings() {
  const [filter, setFilter] = useState("all");
  const [refunding, setRefunding] = useState<Booking | null>(null);
  const { data = [], isLoading, refetch } = useQuery<Booking[]>({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const list = data.filter((b) => filter === "all" || b.mode === filter || b.status === filter);

  const change = async (id: string, status: string) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(status === "confirmed" ? "Reserva confirmada y fecha bloqueada" : "Estado actualizado");
    } catch (e) {
      const d = errDetail(e);
      toast.error(d === "date_unavailable" ? "La fecha ya está bloqueada para este yate" : d);
    }
    refetch();
  };

  return (
    <div className="max-w-6xl" data-testid="admin-bookings-page">
      <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Reservas y solicitudes</h1>
      <p className="mt-2 text-sm text-slate-500">Confirmar una solicitud bloquea automáticamente la fecha del yate. Cancelar la libera. Las reservas pagadas se cancelan con "Cancelar y reembolsar" (aplica la política: 100% ≥15 días, 50% 7–14, 0% &lt;7).</p>
      <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`admin-bookings-filter-${f.k}`}
            className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${filter === f.k ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}>
            {f.label} <span className="opacity-60">({data.filter((b) => f.k === "all" || b.mode === f.k || b.status === f.k).length})</span>
          </button>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-[#00A8A0]" /> :
          list.length === 0 ? <p className="text-slate-500 py-10" data-testid="admin-bookings-empty">No hay reservas en esta vista.</p> :
            list.map((b) => <BookingRow key={b.id} b={b} onChange={change} onRefund={setRefunding} />)}
      </div>
      <RefundDialog key={refunding?.id || "none"} booking={refunding} onClose={() => setRefunding(null)} onDone={() => { setRefunding(null); refetch(); }} />
    </div>
  );
}
